import {LatLngDto} from '../../../../shared/dtos/estimate-drag-path.dto';

/*
 * Work item pin layout (SQ-621): the port of Android's core/geo/MapSpiderfy.kt,
 * which was itself the port of this app's shared/misc/spiderfy-helpers.ts. Every
 * pin sits just north of its true spot with a red arrow down to it, and pins
 * that would overlap on screen are moved apart edge to edge (circles, arrow
 * shafts and crossings all count). All geometry is in CSS px at a zoom, and the
 * Web Mercator projection is done here, so the layout is a pure function of the
 * items and the zoom, as on Android.
 */

export const MARKER_DIAMETER_PX = 38;
export const MARKER_RADIUS_PX = MARKER_DIAMETER_PX / 2;
/** The default arrow's length past the circle's edge. */
export const DOWN_ARROW_LENGTH_PX = 21;

const CIRCLE_EDGE_GAP = 2;
const MIN_CENTER_SEPARATION = MARKER_DIAMETER_PX + CIRCLE_EDGE_GAP;
const ARROW_STROKE_HALF = 1;
const CIRCLE_HIT = MARKER_RADIUS_PX + ARROW_STROKE_HALF;
const SHAFT_MIN_SEPARATION = 2 * ARROW_STROKE_HALF + CIRCLE_EDGE_GAP;
const NUDGE_CLEARANCE_ITERS = 32;

export interface SpiderItem {
  id: number;
  lat: number;
  lng: number;
}

/** Where one work item draws: its circle at lat/lng, its arrow from base (on the circle) to tip (its saved spot). */
export interface SpiderDisplay {
  id: number;
  lat: number;
  lng: number;
  trueLat: number;
  trueLng: number;
  baseLat: number;
  baseLng: number;
  tipLat: number;
  tipLng: number;
  isSpiderfied: boolean;
}

interface Px { x: number; y: number }

const clampLat = (lat: number) => Math.min(85.05112878, Math.max(-85.05112878, lat));

const worldPx = (lat: number, lng: number, zoom: number): Px => {
  const scale = 256 * Math.pow(2, zoom);
  const sinY = Math.sin(clampLat(lat) * Math.PI / 180);
  return {x: scale * (0.5 + lng / 360), y: scale * (0.5 - Math.log((1 + sinY) / (1 - sinY)) / (4 * Math.PI))};
};

const fromWorldPx = (p: Px, zoom: number): LatLngDto => {
  const scale = 256 * Math.pow(2, zoom);
  const lng = (p.x / scale - 0.5) * 360;
  const n = Math.PI * (1 - 2 * p.y / scale);
  return {lat: (2 * Math.atan(Math.exp(n)) - Math.PI / 2) * 180 / Math.PI, lng};
};

/** Offsets a point by east/north CSS px at a zoom (world y grows south). */
export const offsetByPx = (base: LatLngDto, eastPx: number, northPx: number, zoom: number): LatLngDto => {
  const p = worldPx(base.lat, base.lng, zoom);
  return fromWorldPx({x: p.x + eastPx, y: p.y - northPx}, zoom);
};

/** The saved spot a circle dropped at `center` stands for: the arrow's tip, straight south of it. */
export const tipFromMarkerCenter = (center: LatLngDto, zoom: number): LatLngDto =>
  offsetByPx(center, 0, -(MARKER_RADIUS_PX + DOWN_ARROW_LENGTH_PX), zoom);

/** The point on the circle's edge closest to the tip: the arrow's base. */
export const arrowBase = (center: LatLngDto, tip: LatLngDto, zoom: number): LatLngDto => {
  const c = worldPx(center.lat, center.lng, zoom);
  const t = worldPx(tip.lat, tip.lng, zoom);
  const dx = t.x - c.x;
  const dy = t.y - c.y;
  const dist = Math.hypot(dx, dy);
  if (dist < 1e-3) return offsetByPx(center, 0, -MARKER_RADIUS_PX, zoom);
  const s = MARKER_RADIUS_PX / dist;
  return fromWorldPx({x: c.x + dx * s, y: c.y + dy * s}, zoom);
};

const singleton = (item: SpiderItem, zoom: number): SpiderDisplay => {
  const tip = {lat: item.lat, lng: item.lng};
  const center = offsetByPx(tip, 0, MARKER_RADIUS_PX + DOWN_ARROW_LENGTH_PX, zoom);
  const base = arrowBase(center, tip, zoom);
  return {
    id: item.id, lat: center.lat, lng: center.lng, trueLat: item.lat, trueLng: item.lng,
    baseLat: base.lat, baseLng: base.lng, tipLat: tip.lat, tipLng: tip.lng, isSpiderfied: false,
  };
};

// ---- Collision geometry (world px) --------------------------------------------------------------

const pxDist = (a: Px, b: Px) => Math.hypot(a.x - b.x, a.y - b.y);

const closestPointOnSegment = (p: Px, a: Px, b: Px): Px => {
  const abx = b.x - a.x;
  const aby = b.y - a.y;
  const abLenSq = abx * abx + aby * aby;
  if (abLenSq < 1e-6) return a;
  const t = Math.min(1, Math.max(0, ((p.x - a.x) * abx + (p.y - a.y) * aby) / abLenSq));
  return {x: a.x + abx * t, y: a.y + aby * t};
};

const pointToSegment = (p: Px, a: Px, b: Px) => pxDist(p, closestPointOnSegment(p, a, b));

const segmentsProperlyIntersect = (a: Px, b: Px, c: Px, d: Px): boolean => {
  const abx = b.x - a.x;
  const aby = b.y - a.y;
  const cdx = d.x - c.x;
  const cdy = d.y - c.y;
  const acx = c.x - a.x;
  const acy = c.y - a.y;
  const denom = abx * cdy - aby * cdx;
  if (Math.abs(denom) < 1e-8) return false;
  const t = (acx * cdy - acy * cdx) / denom;
  const u = (acx * aby - acy * abx) / denom;
  const eps = 1e-4;
  return t > eps && t < 1 - eps && u > eps && u < 1 - eps;
};

const segmentToSegment = (a: Px, b: Px, c: Px, d: Px) => segmentsProperlyIntersect(a, b, c, d)
  ? 0
  : Math.min(pointToSegment(a, c, d), pointToSegment(b, c, d), pointToSegment(c, a, b), pointToSegment(d, a, b));

const collide = (aCenter: Px, aBase: Px, aTip: Px, bCenter: Px, bBase: Px, bTip: Px): boolean => {
  if (pxDist(aCenter, bCenter) < MIN_CENTER_SEPARATION) return true;
  if (pointToSegment(aCenter, bBase, bTip) < CIRCLE_HIT) return true;
  if (pointToSegment(bCenter, aBase, aTip) < CIRCLE_HIT) return true;
  if (segmentsProperlyIntersect(aBase, aTip, bBase, bTip)) return true;
  // Stacked tips: the gap cannot be met, and chasing it would push forever.
  if (pxDist(aTip, bTip) < SHAFT_MIN_SEPARATION) return false;
  return segmentToSegment(aBase, aTip, bBase, bTip) < SHAFT_MIN_SEPARATION;
};

/** Moves circles (tips stay) until nothing overlaps. `frozenId` stays put: the pin just dropped. */
export function nudgeClearOfOverlaps(displays: SpiderDisplay[], zoom: number, frozenId?: number): SpiderDisplay[] {
  if (displays.length < 2) return displays;
  const working = displays.map(d => ({...d}));

  const pixelsFor = (d: SpiderDisplay): [Px, Px, Px] => {
    const tip = {lat: d.tipLat, lng: d.tipLng};
    const center = {lat: d.lat, lng: d.lng};
    const base = arrowBase(center, tip, zoom);
    return [worldPx(center.lat, center.lng, zoom), worldPx(base.lat, base.lng, zoom), worldPx(tip.lat, tip.lng, zoom)];
  };
  const moveIndex = (idx: number, eastPx: number, northPx: number) => {
    const d = working[idx];
    const next = offsetByPx({lat: d.lat, lng: d.lng}, eastPx, northPx, zoom);
    working[idx] = {...d, lat: next.lat, lng: next.lng};
  };
  const pushAlong = (aIdx: number, bIdx: number, ux: number, uy: number, need: number): boolean => {
    if (need <= 0) return false;
    const half = need / 2;
    moveIndex(aIdx, -ux * half, uy * half);
    moveIndex(bIdx, ux * half, -uy * half);
    return true;
  };
  // With one end frozen, the other slides sideways to its own arrow rather than along it.
  const scoochSidewaysFromFrozen = (movableIdx: number, frozenIdx: number, step: number): boolean => {
    if (step <= 0) return false;
    const [movCenter, , movTip] = pixelsFor(working[movableIdx]);
    const [frzCenter] = pixelsFor(working[frozenIdx]);
    let sx = movCenter.x - movTip.x;
    let sy = movCenter.y - movTip.y;
    const slen = Math.hypot(sx, sy);
    if (slen < 1e-3) { sx = 0; sy = -1; } else { sx /= slen; sy /= slen; }
    let px = -sy;
    let py = sx;
    const fx = frzCenter.x - movCenter.x;
    const fy = frzCenter.y - movCenter.y;
    const side = fx * px + fy * py;
    if (side > 0 || (Math.abs(side) < 1e-3 && fx > 0)) {
      px = -px;
      py = -py;
    } else if (Math.abs(side) < 1e-3 && fx === 0) {
      px = 1;
      py = 0;
    }
    moveIndex(movableIdx, px * step, -py * step);
    return true;
  };

  const resolvePair = (aIdx: number, bIdx: number): boolean => {
    const a = working[aIdx];
    const b = working[bIdx];
    const [aCenter, aBase, aTip] = pixelsFor(a);
    const [bCenter, bBase, bTip] = pixelsFor(b);
    if (!collide(aCenter, aBase, aTip, bCenter, bBase, bTip)) return false;

    const freezeA = frozenId !== undefined && a.id === frozenId;
    const freezeB = frozenId !== undefined && b.id === frozenId;
    if (freezeA && freezeB) return false;
    if (freezeA !== freezeB) {
      const movableIdx = freezeA ? bIdx : aIdx;
      const frozenIdx = freezeA ? aIdx : bIdx;
      const centerDist = pxDist(freezeA ? bCenter : aCenter, freezeA ? aCenter : bCenter);
      if (centerDist < MIN_CENTER_SEPARATION) {
        const lateralNeed = Math.sqrt(Math.max(0, MIN_CENTER_SEPARATION ** 2 - centerDist ** 2));
        return scoochSidewaysFromFrozen(movableIdx, frozenIdx, Math.max(lateralNeed, 1.5));
      }
      return scoochSidewaysFromFrozen(movableIdx, frozenIdx, 2);
    }

    let dist = pxDist(aCenter, bCenter);
    if (dist < MIN_CENTER_SEPARATION) {
      let ux = 1;
      let uy = 0;
      if (dist >= 1e-3) {
        ux = (bCenter.x - aCenter.x) / dist;
        uy = (bCenter.y - aCenter.y) / dist;
      } else {
        dist = 0;
      }
      return pushAlong(aIdx, bIdx, ux, uy, MIN_CENTER_SEPARATION - dist);
    }

    const aHit = pointToSegment(bCenter, aBase, aTip);
    if (aHit < CIRCLE_HIT) {
      const closest = closestPointOnSegment(bCenter, aBase, aTip);
      let dx = bCenter.x - closest.x;
      let dy = bCenter.y - closest.y;
      let len = Math.hypot(dx, dy);
      if (len < 1e-3) {
        dx = bCenter.x - aCenter.x;
        dy = bCenter.y - aCenter.y;
        len = Math.hypot(dx, dy) || 1;
      }
      return pushAlong(aIdx, bIdx, dx / len, dy / len, CIRCLE_HIT - aHit + 0.5);
    }

    const bHit = pointToSegment(aCenter, bBase, bTip);
    if (bHit < CIRCLE_HIT) {
      const closest = closestPointOnSegment(aCenter, bBase, bTip);
      let dx = aCenter.x - closest.x;
      let dy = aCenter.y - closest.y;
      let len = Math.hypot(dx, dy);
      if (len < 1e-3) {
        dx = aCenter.x - bCenter.x;
        dy = aCenter.y - bCenter.y;
        len = Math.hypot(dx, dy) || 1;
      }
      return pushAlong(aIdx, bIdx, -dx / len, -dy / len, CIRCLE_HIT - bHit + 0.5);
    }

    const shaftDist = segmentToSegment(aBase, aTip, bBase, bTip);
    if (shaftDist < SHAFT_MIN_SEPARATION) {
      const onB = closestPointOnSegment(aBase, bBase, bTip);
      const onA = closestPointOnSegment(onB, aBase, aTip);
      let dx = onB.x - onA.x;
      let dy = onB.y - onA.y;
      let len = Math.hypot(dx, dy);
      if (len < 1e-3 && dist >= 1e-3) {
        dx = bCenter.x - aCenter.x;
        dy = bCenter.y - aCenter.y;
        len = dist;
      } else if (len < 1e-3) {
        dx = 1;
        dy = 0;
        len = 1;
      }
      return pushAlong(aIdx, bIdx, dx / len, dy / len, SHAFT_MIN_SEPARATION - shaftDist + 0.5);
    }
    return false;
  };

  for (let pass = 0; pass < NUDGE_CLEARANCE_ITERS; pass++) {
    let moved = false;
    for (let i = 0; i < working.length; i++) {
      for (let j = i + 1; j < working.length; j++) {
        if (resolvePair(i, j)) moved = true;
      }
    }
    if (!moved) break;
  }

  return working.map(d => {
    const tip = {lat: d.tipLat, lng: d.tipLng};
    const center = {lat: d.lat, lng: d.lng};
    const base = arrowBase(center, tip, zoom);
    const rest = offsetByPx(tip, 0, MARKER_RADIUS_PX + DOWN_ARROW_LENGTH_PX, zoom);
    const isDefault = Math.abs(center.lat - rest.lat) < 1e-7 && Math.abs(center.lng - rest.lng) < 1e-7;
    return {...d, baseLat: base.lat, baseLng: base.lng, isSpiderfied: frozenId !== undefined && d.id === frozenId ? d.isSpiderfied : !isDefault};
  });
}

/** Every item at its default spot, then moved apart. */
export const spiderLayout = (items: SpiderItem[], zoom: number, frozenId?: number): SpiderDisplay[] =>
  nudgeClearOfOverlaps(items.map(i => singleton(i, zoom)), zoom, frozenId);
